import React, { useEffect, useMemo, useRef, useState } from "react";
import { MagnifyingGlass, MapPin, CloudRain, X, Compass, Star, Plus, Warning, Plant as PlantIcon } from "@phosphor-icons/react";
import { toast } from "sonner";
import { searchCity, getForecast } from "@/lib/weather";
import { listFavorites, addFavorite, removeFavorite } from "@/lib/favorites";
import { evaluateDayStress } from "@/lib/plantStress";
import HourlyTrend from "@/components/HourlyTrend";

// Map Open-Meteo WMO code -> emoji (kept intentionally simple, no icon library needed)
function codeIcon(code) {
  if (code == null) return "◌";
  if (code === 0) return "☀️";
  if ([1, 2].includes(code)) return "🌤️";
  if (code === 3) return "☁️";
  if ([45, 48].includes(code)) return "🌫️";
  if ([51, 53, 55, 56, 57].includes(code)) return "🌦️";
  if ([61, 63, 65, 80, 81, 82].includes(code)) return "🌧️";
  if ([66, 67].includes(code)) return "🌧️";
  if ([71, 73, 75, 77, 85, 86].includes(code)) return "🌨️";
  if ([95, 96, 99].includes(code)) return "⛈️";
  return "◌";
}

function dayLabel(iso, idx) {
  if (idx === 0) return "Today";
  try {
    const d = new Date(iso + "T00:00:00");
    return d.toLocaleDateString(undefined, { weekday: "short" });
  } catch {
    return iso;
  }
}

export default function WeatherExtras({ lat, lon, locationLabel, onPickLocation, plants = [], isAuthed = true }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [searching, setSearching] = useState(false);
  const [daily, setDaily] = useState([]);
  const [forecastLoading, setForecastLoading] = useState(false);
  const [forecastWarning, setForecastWarning] = useState("");
  const [favorites, setFavorites] = useState([]);
  const [savingFav, setSavingFav] = useState(false);
  const debounceRef = useRef(null);
  const boxRef = useRef(null);

  // Load favourites once
  useEffect(() => {
    if (!isAuthed) return;
    (async () => {
      setFavorites(await listFavorites());
    })();
  }, [isAuthed]);

  const activeFav = useMemo(() => {
    if (lat == null || lon == null) return null;
    const rLat = Math.round(lat * 100) / 100;
    const rLon = Math.round(lon * 100) / 100;
    return favorites.find((f) =>
      Math.round(f.latitude * 100) / 100 === rLat && Math.round(f.longitude * 100) / 100 === rLon
    ) || null;
  }, [favorites, lat, lon]);

  const canSaveCurrent = isAuthed && lat != null && lon != null && !activeFav && !!locationLabel;

  const handleSaveFavorite = async () => {
    if (!canSaveCurrent || savingFav) return;
    setSavingFav(true);
    try {
      const first = (locationLabel || "").split(",")[0].trim();
      const parts = (locationLabel || "").split(",").map((p) => p.trim());
      const data = await addFavorite({
        label: locationLabel,
        latitude: lat,
        longitude: lon,
        name: first || null,
        country: parts.length > 1 ? parts[parts.length - 1] : null,
      });
      setFavorites(data.items || []);
      if (data.warning) {
        toast.message(data.warning);
      } else {
        toast.success(`Saved ${first || "location"} to favourites`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save favourite");
    } finally {
      setSavingFav(false);
    }
  };

  const handleRemoveFavorite = async (id, e) => {
    e?.stopPropagation?.();
    try {
      const items = await removeFavorite(id);
      setFavorites(items);
    } catch {
      toast.error("Couldn't remove favourite");
    }
  };

  // Per-day stress preview across the user's plants
  const stressByDay = useMemo(() => {
    if (!plants?.length || !daily?.length) return [];
    return daily.map((d) => {
      const affected = [];
      for (const p of plants) {
        const breaches = evaluateDayStress(d, p.thresholds || {});
        if (breaches.length) {
          affected.push({ plant_id: p.plant_id || p.id, common_name: p.common_name || p.name || "Plant", breaches });
        }
      }
      return { date: d.date, affected };
    });
  }, [plants, daily]);

  const stressSummary = useMemo(() => {
    return stressByDay.filter((d) => d.affected.length > 0);
  }, [stressByDay]);

  // Debounced city search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const term = q.trim();
    if (term.length < 2) {
      setResults([]);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const results = await searchCity(term);
        setResults(results || []);
        setShowResults(true);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [q]);

  // Close dropdown on outside click
  useEffect(() => {
    const onClick = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setShowResults(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  // Load 7-day forecast whenever lat/lon change
  useEffect(() => {
    if (lat == null || lon == null) {
      setDaily([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setForecastLoading(true);
      setForecastWarning("");
      try {
        const data = await getForecast(lat, lon);
        if (cancelled) return;
        setDaily(data.daily || []);
        if (data.warning) setForecastWarning(data.warning);
      } catch {
        if (!cancelled) setForecastWarning("Forecast temporarily unavailable.");
      } finally {
        if (!cancelled) setForecastLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [lat, lon]);

  const handlePick = (r) => {
    setQ(r.label);
    setShowResults(false);
    onPickLocation?.({
      latitude: r.latitude,
      longitude: r.longitude,
      label: r.label,
    });
  };

  return (
    <div className="space-y-4" data-testid="weather-extras">
      {/* Favourites row */}
      {isAuthed && (
        <div className="glass rounded-2xl p-4" data-testid="favorites-row">
          <div className="flex items-center justify-between mb-2">
            <div className="font-mono-data text-mute flex items-center gap-1">
              <Star size={12} weight="duotone" /> favourite locations
            </div>
            {canSaveCurrent && (
              <button
                type="button"
                onClick={handleSaveFavorite}
                disabled={savingFav}
                data-testid="save-favorite-btn"
                className="text-xs text-forest hover:underline inline-flex items-center gap-1"
              >
                <Plus size={11} weight="bold" />
                {savingFav ? "saving…" : `save ${(locationLabel || "").split(",")[0].trim()}`}
              </button>
            )}
          </div>
          {favorites.length === 0 ? (
            <div className="text-xs text-mute">
              {canSaveCurrent
                ? "Pin cities you care about to switch between them in one tap."
                : "Pick a city below or share your location, then save it here."}
            </div>
          ) : (
            <div className="flex flex-wrap gap-1.5" data-testid="favorite-chips">
              {favorites.map((f) => {
                const active = activeFav?.id === f.id;
                return (
                  <button
                    key={f.id}
                    type="button"
                    onClick={() => onPickLocation?.({ latitude: f.latitude, longitude: f.longitude, label: f.label })}
                    data-testid="favorite-chip"
                    className={`group inline-flex items-center gap-1 pl-2.5 pr-1 py-1 rounded-full border text-xs transition
                      ${active
                        ? "bg-forest text-white border-forest"
                        : "bg-white text-ink border-sand-200 hover:border-forest/40"}`}
                    title={f.label}
                  >
                    <MapPin size={10} weight="duotone" />
                    <span className="max-w-[160px] truncate">{f.name || f.label}</span>
                    <span
                      role="button"
                      aria-label={`remove ${f.name || f.label}`}
                      onClick={(e) => handleRemoveFavorite(f.id, e)}
                      data-testid="favorite-chip-remove"
                      className={`ml-1 w-4 h-4 rounded-full flex items-center justify-center transition
                        ${active ? "bg-white/20 hover:bg-white/30 text-white" : "text-mute hover:bg-sand-100 hover:text-ink"}`}
                    >
                      <X size={9} weight="bold" />
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* City search */}
      <div className="glass rounded-2xl p-4 relative z-30" ref={boxRef}>
        <div className="font-mono-data text-mute mb-2 flex items-center gap-1">
          <MagnifyingGlass size={12} weight="duotone" /> search a city
        </div>
        <div className="relative">
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onFocus={() => results.length > 0 && setShowResults(true)}
            placeholder="Type a city, e.g. Bengaluru, Nairobi, Lisbon…"
            data-testid="city-search-input"
            className="w-full bg-white border border-sand-200 rounded-xl pl-9 pr-9 py-2.5 text-sm outline-none focus:ring-2 focus:ring-forest"
          />
          <MagnifyingGlass size={14} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-mute" />
          {q && (
            <button
              type="button"
              onClick={() => {
                setQ("");
                setResults([]);
                setShowResults(false);
              }}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full hover:bg-sand-100 flex items-center justify-center text-mute"
              aria-label="Clear city search"
              data-testid="city-search-clear"
            >
              <X size={12} weight="bold" />
            </button>
          )}
        </div>
        {showResults && (
          <div className="absolute left-4 right-4 top-full mt-1 bg-white border border-sand-200 rounded-xl shadow-lg z-40 overflow-hidden" data-testid="city-search-results">
            {searching && <div className="px-3 py-2 text-sm text-mute">searching…</div>}
            {!searching && results.length === 0 && (
              <div className="px-3 py-2 text-sm text-mute">No matches. Try a different spelling.</div>
            )}
            {results.map((r) => (
              <button
                key={`${r.latitude}-${r.longitude}`}
                onClick={() => handlePick(r)}
                data-testid="city-search-result"
                className="w-full text-left px-3 py-2 text-sm hover:bg-sand-100 flex items-center gap-2 border-b last:border-b-0 border-sand-100"
              >
                <MapPin size={12} weight="duotone" className="text-forest shrink-0" />
                <span className="truncate">{r.label}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* 7-day forecast */}
      <div className="glass rounded-2xl p-4" data-testid="forecast-panel">
        <div className="flex items-center justify-between mb-3">
          <div className="font-mono-data text-mute flex items-center gap-1">
            <Compass size={12} weight="duotone" /> 7-day forecast
          </div>
          {locationLabel && <div className="text-xs text-mute truncate max-w-[55%]">{locationLabel}</div>}
        </div>
        {forecastLoading && daily.length === 0 && (
          <div className="text-sm text-mute animate-pulse-soft">loading forecast…</div>
        )}
        {!forecastLoading && forecastWarning && daily.length === 0 && (
          <div className="text-sm text-terra">{forecastWarning}</div>
        )}
        {daily.length > 0 && (
          <div className="grid grid-cols-7 gap-1.5" data-testid="forecast-grid">
            {daily.slice(0, 7).map((d, i) => {
              const stress = stressByDay[i]?.affected || [];
              const stressCount = stress.length;
              const tooltip = stressCount
                ? stress.map((s) => `${s.common_name}: ${s.breaches.join(", ")}`).join("\n")
                : `${d.weather_description || ""} • precip ${d.precipitation_probability_pct ?? 0}%`;
              return (
                <div
                  key={d.date}
                  className={`relative bg-white/70 border rounded-xl p-2 text-center ${stressCount ? "border-terra/60 ring-1 ring-terra/30" : "border-sand-200"}`}
                  title={tooltip}
                  data-testid="forecast-day"
                >
                  {stressCount > 0 && (
                    <div
                      className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-terra text-white text-[10px] font-bold flex items-center justify-center shadow"
                      data-testid="forecast-day-stress-badge"
                      aria-label={`${stressCount} plant${stressCount > 1 ? "s" : ""} at risk`}
                    >
                      {stressCount}
                    </div>
                  )}
                  <div className="font-mono-data text-[10px] text-mute uppercase">{dayLabel(d.date, i)}</div>
                  <div className="text-2xl leading-none my-1" aria-label={d.weather_description || "weather"}>
                    {codeIcon(d.weather_code)}
                  </div>
                  <div className="font-display text-sm text-ink leading-none">
                    {d.temp_max_c != null ? Math.round(d.temp_max_c) : "—"}°
                  </div>
                  <div className="text-[10px] text-mute mt-0.5">
                    {d.temp_min_c != null ? Math.round(d.temp_min_c) : "—"}°
                  </div>
                  <div className="flex items-center justify-center gap-0.5 mt-1 text-[10px] text-forest">
                    <CloudRain size={9} weight="duotone" />
                    {d.precipitation_probability_pct ?? 0}%
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Stress summary (only when plants + daily are both available and there are risks) */}
        {plants?.length > 0 && daily?.length > 0 && stressSummary.length > 0 && (
          <div className="mt-4 bg-terra/5 border border-terra/30 rounded-xl p-3" data-testid="stress-summary">
            <div className="flex items-center gap-2 mb-2 text-terra">
              <Warning size={14} weight="duotone" />
              <span className="font-mono-data text-[11px] uppercase tracking-wider">plant risk forecast</span>
            </div>
            <ul className="space-y-1.5">
              {stressSummary.map((s) => (
                <li key={s.date} className="text-xs text-ink flex items-start gap-2" data-testid="stress-summary-day">
                  <span className="font-mono-data text-mute uppercase shrink-0 w-16">{dayLabel(s.date, daily.findIndex((d) => d.date === s.date))}</span>
                  <div className="flex flex-wrap gap-1">
                    {s.affected.map((a) => (
                      <span key={`${s.date}-${a.plant_id}`} className="inline-flex items-center gap-1 bg-white border border-sand-200 rounded-full px-2 py-0.5 text-[11px]">
                        <PlantIcon size={10} weight="duotone" className="text-forest" />
                        <span className="font-medium">{a.common_name}</span>
                        <span className="text-mute">· {a.breaches.join(" · ")}</span>
                      </span>
                    ))}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
        {plants?.length > 0 && daily?.length > 0 && stressSummary.length === 0 && (
          <div className="mt-3 text-xs text-mute" data-testid="stress-summary-clear">
            No threshold breaches expected for your plants in the next 7 days.
          </div>
        )}
      </div>

      {/* Hourly trend */}
      <HourlyTrend lat={lat} lon={lon} />
    </div>
  );
}
